import React from 'react';
import { Link } from 'react-router-dom';
import styles from './About.module.css';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import ChatBot from '../components/ChatBot';
import aboutImage from '../assets/Images/about-image.jpg';

const About = () => {
    const values = [
        {
            icon: '🎯',
            title: 'Excellence',
            description: 'We pursue perfection in every project, delivering exceptional quality that exceeds client expectations and sets industry standards.'
        },
        {
            icon: '🤝',
            title: 'Collaboration',
            description: 'We believe great things happen when we work together. Your vision combined with our expertise creates extraordinary results.'
        },
        {
            icon: '💡',
            title: 'Innovation',
            description: 'We stay ahead of technological trends, embracing cutting-edge solutions to solve complex challenges and drive digital transformation.'
        },
        {
            icon: '❤️',
            title: 'Integrity',
            description: 'We build trust through transparency, honesty, and ethical practices. Your success is our success, and we treat your business as our own.'
        },
        {
            icon: '⚡',
            title: 'Efficiency',
            description: 'We optimize processes and leverage technology to deliver high-quality results within agreed timelines and budgets.'
        },
        {
            icon: '🌍',
            title: 'Global Mindset',
            description: 'We understand diverse markets and cultures, creating solutions that resonate with audiences worldwide.'
        }
    ];

    const stats = [
        { number: '50+', label: 'Successful Projects', description: 'Delivered on time and within scope' },
        { number: '25+', label: 'Satisfied Clients', description: 'From startups to established enterprises' },
        { number: '5+', label: 'Years of Excellence', description: 'Experience in digital innovation' },
        { number: '100%', label: 'Client Retention', description: 'Ongoing partnerships and support' }
    ];

    const teamStrengths = [
        {
            title: 'Technical Expertise',
            description: 'Our team comprises skilled developers, designers, and strategists with diverse technical backgrounds and certifications.'
        },
        {
            title: 'Creative Vision',
            description: 'We blend technical prowess with creative thinking to deliver unique and engaging digital experiences that stand out.'
        },
        {
            title: 'Strategic Thinking',
            description: 'We don\'t just build solutions; we develop comprehensive strategies that align with your long-term business objectives.'
        },
        {
            title: 'Continuous Learning',
            description: 'We stay updated with the latest technologies and methodologies to deliver cutting-edge, future-proof solutions.'
        },
        {
            title: 'Client-Centric Approach',
            description: 'Your goals become our goals. We prioritize understanding your needs and delivering solutions that drive real business value.'
        }
    ];

    return (
        <div className={styles.aboutPage}>
            {/* Hero Section */}
            <section className={styles.hero}>
                <div className={styles.container}>
                    <h1>Building Digital Excellence Since 2019</h1>
                    <p className={styles.subtitle}>
                        We transform ideas into powerful digital solutions that drive growth, 
                        enhance user experiences, and create lasting impact for businesses worldwide.
                    </p>
                </div>
            </section>

            {/* Story Section */}
            <section className={styles.storySection}>
                <div className={styles.container}>
                    <div className={styles.storyContent}>
                        <div className={styles.storyText}>
                            <h2>Our Journey</h2>
                            <p>
                                Founded in 2019, Apexbit emerged from a shared passion for technology 
                                and a vision to help businesses thrive in the digital landscape. What 
                                began as a small team of dedicated developers has evolved into a 
                                comprehensive digital agency serving clients across diverse industries.
                            </p>
                            <p>
                                Our journey has been shaped by countless collaborations, challenges overcome, 
                                and successes celebrated. Each project has taught us valuable lessons and 
                                strengthened our commitment to delivering exceptional results.
                            </p>
                            <p>
                                Today, we stand as a trusted partner for businesses seeking to innovate, 
                                transform, and excel in their digital endeavours. Our story continues to 
                                unfold with each new partnership and every solution we create.
                            </p>
                        </div>
                        <div className={styles.storyImage}>
                            <img 
                                src={aboutImage} 
                                alt="Our collaborative workspace where innovation thrives" 
                                className={styles.aboutImage}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Team Strengths Section - 5 cards */}
            <section className={styles.strengthsSection}>
                <div className={styles.container}>
                    <div className={styles.sectionHeader}>
                        <h2>What Makes Us Different</h2>
                        <p>Our unique approach combines technical excellence with strategic thinking</p>
                    </div>
                    <div className={styles.strengthsGrid}>
                        {teamStrengths.map((strength, index) => (
                            <Card key={index} variant="elevated" padding="medium" className={styles.strengthCard}>
                                <h3>{strength.title}</h3>
                                <p>{strength.description}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Values Section - 6 cards */}
            <section className={styles.valuesSection}>
                <div className={styles.container}>
                    <div className={styles.sectionHeader}>
                        <h2>Our Core Values</h2>
                        <p>The principles that guide every decision we make and every solution we deliver</p>
                    </div>
                    <div className={styles.valuesGrid}>
                        {values.map((value, index) => (
                            <Card key={index} variant="default" padding="large" className={styles.valueCard}>
                                <div className={styles.valueIcon}>{value.icon}</div>
                                <h3>{value.title}</h3>
                                <p>{value.description}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Stats Section - 4 cards */}
            <section className={styles.statsSection}>
                <div className={styles.container}>
                    <div className={styles.sectionHeader}>
                        <h2>Our Impact in Numbers</h2>
                        <p>Quantifying our journey of growth and client success</p>
                    </div>
                    <div className={styles.statsGrid}>
                        {stats.map((stat, index) => (
                            <div key={index} className={styles.statItem}>
                                <div className={styles.statNumber}>{stat.number}</div>
                                <div className={styles.statLabel}>{stat.label}</div>
                                <div className={styles.statDescription}>{stat.description}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className={styles.ctaSection}>
                <div className={styles.container}>
                    <h2>Ready to Write Your Success Story?</h2>
                    <p>Join the growing list of businesses that have transformed their digital presence with Apexbit</p>
                    <div className={styles.ctaButtons}>
                        <Button as={Link} to="/contact" variant="primary" size="large">
                            Start Your Journey
                        </Button>
                    </div>
                </div>
            </section>
            <ChatBot />
        </div>
    );
};

export default About;
